import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import * as fromAnnouncementsActions from '../actions/announcements.actions';
import { ANNOUNCEMENTS_FEATURE_KEY } from '../constants/state-keys';
import { GetAnnouncementDto } from '../dtos/get-announcement-dto.interface';
import { IAnnouncementsFeatureState } from '../interfaces/states/announcements-feature-state.interface';

export function selectAnnouncementId(a: GetAnnouncementDto): string {
    return a.id;
}

export const adapter: EntityAdapter<GetAnnouncementDto> = createEntityAdapter<GetAnnouncementDto>({
    selectId: selectAnnouncementId,
});

export const selectFeature = createFeatureSelector<IAnnouncementsFeatureState>(ANNOUNCEMENTS_FEATURE_KEY);

export const { selectAll, selectEntities, selectIds, selectTotal } = adapter.getSelectors();

export const selectAllAnnouncements = createSelector(selectFeature, selectAll);

export const selectAnnouncementsEntities = createSelector(selectFeature, selectEntities);

export const getUserAnnouncements = (state: IAnnouncementsFeatureState) => state.userAnnouncements;

export const selectUserAnnouncements = createSelector(selectFeature, getUserAnnouncements);

export const getLoading = (state: IAnnouncementsFeatureState) => state.loading;

export const selectLoading = createSelector(selectFeature, getLoading);

export const getError = (state: IAnnouncementsFeatureState) => state.error;

export const selectError = createSelector(selectFeature, getError);

export const getSelectedAnnouncementId = (state: IAnnouncementsFeatureState) => state.selectedAnnouncementId;

export const selectSelectedAnnouncementId = createSelector(selectFeature, getSelectedAnnouncementId);

export const selectSelectedAnnouncement = createSelector(
    selectFeature,
    selectAnnouncementsEntities,
    (state, entities) => (state.selectedAnnouncementId ? entities[state.selectedAnnouncementId] || null : null),
);

export const selectAnnouncementsState = createFeatureSelector<IAnnouncementsFeatureState>(ANNOUNCEMENTS_FEATURE_KEY);

const initialState: IAnnouncementsFeatureState = adapter.getInitialState({
    userAnnouncements: [],
    selectedAnnouncementId: null,
    loading: false,
    error: null,
    dismissingAnnouncementId: null,
});

export const announcementsReducer = createReducer(
    initialState,

    // Get All Announcements
    on(fromAnnouncementsActions.announcementsActions.getAllAnnouncements, (state) => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(fromAnnouncementsActions.announcementsActions.getAllAnnouncementsSuccess, (state, action) => {
        return adapter.setAll(action.payload, {
            ...state,
            loading: false,
            error: null,
        });
    }),
    on(fromAnnouncementsActions.announcementsActions.getAllAnnouncementsFailed, (state, action) => ({
        ...state,
        loading: false,
        error: action.payload,
    })),

    // Create Announcement
    on(fromAnnouncementsActions.announcementsActions.createAnnouncement, (state) => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(fromAnnouncementsActions.announcementsActions.createAnnouncementSuccess, (state, action) => {
        return adapter.addOne(action.payload, {
            ...state,
            loading: false,
            error: null,
        });
    }),
    on(fromAnnouncementsActions.announcementsActions.createAnnouncementFailed, (state, action) => ({
        ...state,
        loading: false,
        error: action.payload,
    })),

    // Update Announcement
    on(fromAnnouncementsActions.announcementsActions.updateAnnouncement, (state) => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(fromAnnouncementsActions.announcementsActions.updateAnnouncementSuccess, (state, action) => {
        return adapter.updateOne(
            { id: action.payload.id, changes: action.payload },
            {
                ...state,
                loading: false,
                error: null,
            },
        );
    }),
    on(fromAnnouncementsActions.announcementsActions.updateAnnouncementFailed, (state, action) => ({
        ...state,
        loading: false,
        error: action.payload,
    })),

    // Delete Announcement
    on(fromAnnouncementsActions.announcementsActions.deleteAnnouncement, (state) => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(fromAnnouncementsActions.announcementsActions.deleteAnnouncementSuccess, (state, action) => {
        return adapter.removeOne(action.payload, {
            ...state,
            loading: false,
            error: null,
        });
    }),
    on(fromAnnouncementsActions.announcementsActions.deleteAnnouncementFailed, (state, action) => ({
        ...state,
        loading: false,
        error: action.payload,
    })),

    // Get User Announcements
    on(fromAnnouncementsActions.announcementsActions.getUserAnnouncements, (state) => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(fromAnnouncementsActions.announcementsActions.getUserAnnouncementsSuccess, (state, action) => ({
        ...state,
        userAnnouncements: action.payload,
        loading: false,
        error: null,
    })),
    on(fromAnnouncementsActions.announcementsActions.getUserAnnouncementsFailed, (state, action) => ({
        ...state,
        loading: false,
        error: action.payload,
    })),

    // Dismiss Announcement
    on(fromAnnouncementsActions.announcementsActions.dismissAnnouncement, (state, action) => ({
        ...state,
        loading: true,
        error: null,
        // Store the announcement ID being dismissed
        dismissingAnnouncementId: action.payload,
    })),
    on(fromAnnouncementsActions.announcementsActions.dismissAnnouncementSuccess, (state) => ({
        ...state,
        loading: false,
        error: null,
        // Remove the dismissed announcement from user announcements
        userAnnouncements: state.userAnnouncements.filter(
            (announcement) => announcement.id !== state.dismissingAnnouncementId,
        ),
        dismissingAnnouncementId: null,
    })),
    on(fromAnnouncementsActions.announcementsActions.dismissAnnouncementFailed, (state, action) => ({
        ...state,
        loading: false,
        error: action.payload,
        dismissingAnnouncementId: null,
    })),

    // Set Selected Announcement
    on(fromAnnouncementsActions.announcementsActions.setSelectedAnnouncement, (state, action) => ({
        ...state,
        selectedAnnouncementId: action.payload,
    })),
);
